import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";

import "./Consultation/Consultation.css";

import { Phase, ConnectionState } from "../types";

import { useGeminiLive } from "../hooks/useGeminiLive";
import { useScriptPlayer } from "../hooks/useScriptPlayer";
import { useFetchBriefing } from "../hooks/useFetchBriefing";

import {
  startConsulting,
  completeConsultation,
  completeBriefing,
} from "../services/api";
import { trackEvent } from "../utils/ga";
import { captureAppException } from "../sentry";

import IdleControls from "./Consultation/IdleControls";
import ReadingControls from "./Consultation/ReadingControls";
import LiveControls from "./Consultation/LiveControls";

import type { ModalConfig } from "../context/ConsultationContext";
import { useConsultation } from "../context/useConsultation";

interface ConsultationButtonsProps {
  isPortrait: boolean;
}

const ConsultationButtons: React.FC<ConsultationButtonsProps> = ({
  isPortrait,
}) => {
  const {
    phase,
    setPhase,
    scoreData,
    briefData,
    flowMode,
    briefingDone,
    setBriefingDone,
    mouthRef,
    setModalConfig,
    setIsThinking,
  } = useConsultation();

  const [isDragging, setIsDragging] = useState(false);
  const currentVolumeRef = useRef(0);
  const progressBgRef = useRef<HTMLDivElement>(null);

  const { t, i18n } = useTranslation();
  const { fetchBriefing } = useFetchBriefing();

  const handleAudioLevel = useCallback(
    (volume: number) => {
      if (!mouthRef.current) return;
      const THRESHOLD = 0.1;
      const INPUT_MAX = 0.5;
      const MAX_OPEN = 0.7;
      const effectiveVol = volume < THRESHOLD ? 0 : volume;
      let ratio = Math.min(effectiveVol / INPUT_MAX, 1.0);
      ratio = Math.pow(ratio, 2);
      currentVolumeRef.current += (ratio - currentVolumeRef.current) * 0.2;
      const scaleY = 1 + currentVolumeRef.current * MAX_OPEN;
      mouthRef.current.style.transform = `scaleY(${scaleY})`;
    },
    [mouthRef],
  );

  const {
    isPlaying: isReading,
    isPaused: isReadingPaused,
    playbackRate: readingPlaybackRate,
    progress: readingProgress,
    error: readingError,
    playAudio,
    togglePause: toggleReadingPause,
    setPlaybackRate: setReadingPlaybackRate,
    seekTo: seekReadingTo,
    stop: stopReading,
  } = useScriptPlayer(handleAudioLevel);

  const handleProgressAction = useCallback(
    (clientX: number) => {
      if (readingProgress.duration === 0 || !progressBgRef.current) return;
      const rect = progressBgRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const targetTime = (x / rect.width) * readingProgress.duration;
      seekReadingTo(targetTime);
    },
    [readingProgress.duration, seekReadingTo],
  );

  const {
    status: liveStatus,
    errorDetail,
    connect: connectLive,
    disconnectSession,
    questionCount,
    maxQuestions,
    questionPhase,
    isPaused: isLivePaused,
    isModelSpeaking,
    togglePause: toggleLivePause,
    startQuestion,
    endQuestion,
    setScoreData: setScoreDataService,
  } = useGeminiLive(handleAudioLevel);

  useEffect(() => {
    if (phase === Phase.READING_SCRIPT && !isReading && !readingError) {
      completeBriefing();
      setBriefingDone(true);
      setPhase(Phase.BRIEFING_COMPLETED);
    }
  }, [phase, isReading, readingError, setPhase, setBriefingDone]);

  // 연결 끊김(비정상 종료) 에러 처리
  useEffect(() => {
    if (liveStatus === ConnectionState.ERROR) {
      setModalConfig({
        isOpen: true,
        title: t("connectionLostTitle"),
        message: errorDetail || t("connectionLostMessage"),
        showProgress: false,
      });
    } else if (liveStatus === ConnectionState.RECONNECTING) {
      setModalConfig({
        isOpen: true,
        title: t("reconnectingTitle"),
        message: t("reconnectingMessage"),
        showProgress: true,
      });
    } else if (liveStatus === ConnectionState.CONNECTED) {
      setModalConfig((prev: ModalConfig) => {
        if (prev.isOpen && prev.title === t("reconnectingTitle")) {
          return { ...prev, isOpen: false };
        }
        return prev;
      });
    }
  }, [liveStatus, t, setModalConfig, errorDetail]);

  // 답변 대기 모달 및 생각 풍선 로직
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (questionPhase === "answering" && !isModelSpeaking && !isLivePaused) {
      // 생각 풍선 즉시 표시
      setIsThinking(true);

      // 3초 이상 지연 시 안내 모달 표시
      timer = setTimeout(() => {
        setModalConfig({
          isOpen: true,
          title: t("aiPreparingTitle"),
          message: t("aiPreparingMessage"),
          showProgress: true,
        });
      }, 3000);
    } else {
      // AI가 말을 시작하거나(isModelSpeaking), 다른 페이즈일 때 풍선 및 모달 제거
      setIsThinking(false);
      setModalConfig((prev: ModalConfig) => {
        if (prev.isOpen && prev.title === t("aiPreparingTitle")) {
          return { ...prev, isOpen: false };
        }
        return prev;
      });
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [
    questionPhase,
    isModelSpeaking,
    isLivePaused,
    setModalConfig,
    t,
    setIsThinking,
  ]);

  // 화면 회전 시 브리핑 일시 정지 처리
  useEffect(() => {
    if (isPortrait && phase === Phase.READING_SCRIPT && !isReadingPaused) {
      toggleReadingPause();
    }
  }, [isPortrait, phase, isReadingPaused, toggleReadingPause]);

  const handleStartBriefing = () => {
    if (briefData?.audio_url) {
      trackEvent("Consultation", "Start Briefing");
      startConsulting();
      setPhase(Phase.READING_SCRIPT);
      playAudio(briefData.audio_url);
    }
  };

  const handleStartLiveConsultation = async () => {
    trackEvent("Consultation", "Start Live", i18n.language);
    if (scoreData) {
      setScoreDataService(scoreData);
    }
    setPhase(Phase.LIVE_CONSULTATION);
    await connectLive(i18n.language);
  };

  const handleEndConsultation = async () => {
    trackEvent("Consultation", "Request End");
    setModalConfig({
      isOpen: true,
      title: t("endConsultation"),
      message: t("askQuitConsultation"),
      showProgress: false,
      type: "confirm",
      onConfirm: async () => {
        trackEvent("Consultation", "Confirm End");
        try {
          completeConsultation();
          await disconnectSession();
          window.close();
        } catch (err: unknown) {
          captureAppException(err, {
            tags: { feature: "consultation_end" },
          });
          const errMsg = err instanceof Error ? err.message : t("processError");
          setModalConfig({
            isOpen: true,
            title: t("errorOccurred"),
            message: errMsg,
            showProgress: false,
            type: "alert",
          });
        }
      },
    });
  };

  const handleSkipBriefing = () => {
    trackEvent("Consultation", "Skip Briefing");
    stopReading();
    setPhase(Phase.BRIEFING_COMPLETED);
  };

  const handleReplayBriefing = () => {
    if (briefData?.audio_url) {
      trackEvent("Consultation", "Replay Briefing");
      setPhase(Phase.READING_SCRIPT);
      playAudio(briefData.audio_url);
    }
  };

  const handleRetryBriefing = async () => {
    trackEvent("Consultation", "Retry Briefing");
    const newData = await fetchBriefing();
    if (newData?.audio_url) {
      playAudio(newData.audio_url);
    }
  };

  return (
    <>
      {phase === Phase.READING_SCRIPT && (
        <ReadingControls
          readingError={readingError}
          readingProgress={readingProgress}
          isReadingPaused={isReadingPaused}
          readingPlaybackRate={readingPlaybackRate}
          canSkip={briefingDone}
          handleSkipBriefing={handleSkipBriefing}
          handleRetryBriefing={handleRetryBriefing}
          toggleReadingPause={toggleReadingPause}
          seekReadingTo={seekReadingTo}
          setReadingPlaybackRate={setReadingPlaybackRate}
          progressBgRef={progressBgRef}
          setIsDragging={setIsDragging}
          handleProgressAction={handleProgressAction}
          isDragging={isDragging}
        />
      )}

      {phase === Phase.LIVE_CONSULTATION && (
        <LiveControls
          liveStatus={liveStatus}
          questionCount={questionCount}
          maxQuestions={maxQuestions}
          questionPhase={questionPhase}
          isLivePaused={isLivePaused}
          startQuestion={() => {
            trackEvent("LiveControls", "Start Question");
            startQuestion();
          }}
          endQuestion={() => {
            trackEvent("LiveControls", "End Question");
            endQuestion();
          }}
          toggleLivePause={() => {
            trackEvent(
              "LiveControls",
              isLivePaused ? "Resume Answering" : "Pause Answering",
            );
            toggleLivePause();
          }}
          handleEndConsultation={handleEndConsultation}
          onRetryConnection={() => {
            trackEvent("LiveControls", "Retry Connection");
            connectLive(i18n.language);
          }}
        />
      )}

      <div className="controls">
        <IdleControls
          phase={phase}
          flowMode={flowMode}
          scoreData={scoreData}
          briefData={briefData}
          handleStartLiveConsultation={handleStartLiveConsultation}
          handleStartBriefing={handleStartBriefing}
          handleReplayBriefing={handleReplayBriefing}
          handleEndConsultation={handleEndConsultation}
        />
      </div>
    </>
  );
};
export default ConsultationButtons;
